import { useState, useCallback } from 'react';

/**
 * CareflowChipInput — add/remove list for phone numbers or emails, controlled
 * via values/onChange (array of strings). Mirrors the validation + formatting
 * used by PhoneListDrawer / EmailListDrawer so the Add Careflow drawer can
 * collect text / voice phone numbers and email recipients into one payload.
 *
 * Props:
 *   kind     — 'phone' | 'email'
 *   values   — array of stored strings (digits for phone, raw email otherwise)
 *   onChange — (next: string[]) => void
 */

const stripToDigits = (val) => String(val).replace(/[^0-9]/g, '');
const isValidPhoneInput = (val) => /^[0-9\- ]*$/.test(val);
const formatPhone = (digits) =>
    digits.length === 10 ? `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}` : digits;
const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const CareflowChipInput = ({ kind, values, onChange }) => {
    const isPhone = kind === 'phone';
    const [draft, setDraft] = useState('');
    const [error, setError] = useState('');

    const handleAdd = useCallback(() => {
        const trimmed = draft.trim();
        if (!trimmed) return;

        if (isPhone) {
            const digits = stripToDigits(trimmed);
            if (digits.length !== 10) { setError('Phone number must be 10 digits.'); return; }
            if (values.includes(digits)) { setError('This number is already in the list.'); return; }
            onChange([...values, digits]);
        } else {
            if (!isValidEmail(trimmed)) { setError('Enter a valid email address.'); return; }
            if (values.includes(trimmed)) { setError('This email is already in the list.'); return; }
            onChange([...values, trimmed]);
        }
        setDraft('');
        setError('');
    }, [draft, isPhone, values, onChange]);

    const handleRemove = useCallback((idx) => {
        onChange(values.filter((_, i) => i !== idx));
    }, [values, onChange]);

    const handleDraftChange = (e) => {
        const val = e.target.value;
        if (isPhone && !isValidPhoneInput(val)) return; // block invalid phone chars
        setDraft(val);
        if (error) setError('');
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); handleAdd(); }
    };

    return (
        <div>
            <div
                style={{
                    maxHeight: '160px',
                    overflowY: 'auto',
                    border: '1px solid var(--color-border, #e5e7eb)',
                    borderRadius: 'var(--radius-md, 0.375rem)',
                    marginBottom: 'var(--space-2, 0.5rem)',
                }}
            >
                {values.length === 0 ? (
                    <div style={{ padding: 'var(--space-2, 0.5rem) var(--space-3, 0.75rem)', textAlign: 'center', color: 'var(--color-text-muted, #6b7280)', fontSize: 'var(--font-size-sm, 0.875rem)' }}>
                        {isPhone ? 'No phone numbers added.' : 'No email addresses added.'}
                    </div>
                ) : (
                    values.map((v, idx) => (
                        <div
                            key={idx}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: 'var(--space-2, 0.5rem) var(--space-3, 0.75rem)',
                                borderBottom: idx < values.length - 1 ? '1px solid var(--color-border, #e5e7eb)' : 'none',
                            }}
                        >
                            <span style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}>{isPhone ? formatPhone(v) : v}</span>
                            <button
                                type="button"
                                onClick={() => handleRemove(idx)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted, #6b7280)', padding: '2px', lineHeight: 1 }}
                                title={isPhone ? 'Remove number' : 'Remove email'}
                            >
                                <i className="fe fe-x" />
                            </button>
                        </div>
                    ))
                )}
            </div>

            <div style={{ display: 'flex', gap: 'var(--space-2, 0.5rem)' }}>
                <input
                    className={`dp-drawer__input${error ? ' is-invalid' : ''}`}
                    type={isPhone ? 'text' : 'email'}
                    placeholder={isPhone ? '555-123-4567' : 'Enter email address'}
                    value={draft}
                    onChange={handleDraftChange}
                    onKeyDown={handleKeyDown}
                    style={{ flex: 1 }}
                />
                <button type="button" className="dp-btn dp-btn--primary dp-btn--sm" onClick={handleAdd}>Add</button>
            </div>
            {error ? <p className="dp-drawer__error">{error}</p> : null}
        </div>
    );
};

export default CareflowChipInput;
